import type { ComponentProps, ReactNode } from 'react';
import React, { PureComponent, createContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { withAnalyticsEvents, type WithAnalyticsEventsProps } from '@atlaskit/analytics-next';

import { VIEW_PAGE_CONTENT_TOOLS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { EndOfPageHideConfirmModal } from '@confluence/page-recommendations/entry-points/EndOfPageHideConfirmModal.ts';

import { Shortcuts, type ShortcutsProps } from './Shortcuts.tsx';
import { ContentToolsButton, type ContentToolsButtonProps } from './ContentToolsButton.tsx';
import { MORE_ACTIONS_DROPDOWN_METRIC } from './perf.config.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SeparatedGroup = styled.div({
	borderBottom: `${token('border.width.selected')} solid ${token('color.border')}`,
	paddingBottom: token('space.025'),
	marginBottom: token('space.025'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-of-type': {
		borderBottom: 0,
		paddingBottom: 0,
		marginBottom: 0,
	},
});

export const ContentToolsDropdownOverrideContext: React.Context<(_: boolean) => void> =
	createContext((_: boolean): void => {});

export class ContentToolsComponentClass extends PureComponent<ContentToolsComponentClassProps> {
	static defaultProps: {
		keyboardShortcuts: never[];
	} = {
		keyboardShortcuts: [],
	};

	state = {
		isMenuChangeAllowed: true,
		isEndOfPageHideConfirmOpen: false,
	};

	activeKeyboardShortcutHandler: null | (() => void) = null;

	setActiveKeyboardShortcutHandler: ShortcutsProps['setActiveKeyboardShortcutHandler'] = (
		activeKeyboardShortcutHandler,
	) => {
		this.activeKeyboardShortcutHandler = activeKeyboardShortcutHandler;
	};

	onPositioned = (): void => {
		MORE_ACTIONS_DROPDOWN_METRIC.stop();
	};

	handleOpenChange: ContentToolsButtonProps['onOpenChange'] = ({ isOpen }) => {
		if (!this.state.isMenuChangeAllowed) {
			return;
		}

		if (isOpen) {
			MORE_ACTIONS_DROPDOWN_METRIC.start();
		}
		this.props.onOpenChange?.(isOpen);
		if (this.activeKeyboardShortcutHandler) {
			this.activeKeyboardShortcutHandler();
			this.activeKeyboardShortcutHandler = null;
		}
		this.fireToggleAnalyticsEvent();
	};

	setMenuChangeAllowed = (allowed: any): void => {
		this.setState({ isMenuChangeAllowed: allowed });
	};

	closeDialog = (): void => {
		this.props.onOpenChange?.(false);
		this.setState({ isMenuChangeAllowed: true });
	};

	setIsEndOfPageHideConfirmOpen = (isOpen: any): void => {
		this.setState({ isEndOfPageHideConfirmOpen: isOpen });
	};

	fireToggleAnalyticsEvent = (): void => {
		const { createAnalyticsEvent } = this.props;
		createAnalyticsEvent?.({
			data: {
				action: 'clicked',
				actionSubject: 'moreIcon',
				source: 'moreActions',
				attributes: {
					componentVersion: 'v2',
				},
			},
			type: 'sendTrackEvent',
		}).fire();
	};

	render(): React.JSX.Element {
		const {
			itemSections,
			keyboardShortcuts,
			contentId,
			contentType,
			spaceKey,
			isExternalCollaborator,
			isAppNavigationShortcut,
			isMenuOpen,
		} = this.props;
		const { isEndOfPageHideConfirmOpen } = this.state;

		return (
			<ContentToolsDropdownOverrideContext.Provider value={this.setMenuChangeAllowed}>
				<Shortcuts
					keyboardShortcuts={keyboardShortcuts}
					setActiveKeyboardShortcutHandler={this.setActiveKeyboardShortcutHandler}
					isAppNavigationShortcut={isAppNavigationShortcut}
				/>
				<Tooltip
					content={
						<FormattedMessage
							id="content-tools.button.tooltip"
							defaultMessage="More actions"
							description="Tooltip for button that opens a menu providing additional actions for a page"
						/>
					}
					hideTooltipOnClick
					position="bottom"
				>
					<ContentToolsButton
						isDisabled={false}
						isOpen={isMenuOpen}
						onOpenChange={this.handleOpenChange}
						onPositioned={this.onPositioned}
						isExternalCollaborator={isExternalCollaborator}
					>
						{itemSections.map((section, index) => {
							// index should be safe as key because it's ordered
							return <SeparatedGroup key={index}>{section}</SeparatedGroup>;
						})}
					</ContentToolsButton>
				</Tooltip>
				<EndOfPageHideConfirmModal
					entityId={contentId}
					entityType={contentType ?? ''}
					spaceKey={spaceKey}
					isOpen={isEndOfPageHideConfirmOpen}
					setIsOpen={this.setIsEndOfPageHideConfirmOpen}
				/>
				<ExperienceSuccess name={VIEW_PAGE_CONTENT_TOOLS_EXPERIENCE} />
			</ContentToolsDropdownOverrideContext.Provider>
		);
	}
}

type ContentToolsComponentClassProps = ContentToolsComponentProps & WithAnalyticsEventsProps;

export const ContentToolsComponent: React.ForwardRefExoticComponent<
	Pick<
		Omit<
			{
				itemSections: ReadonlyArray<NonNullable<ReactNode>>;
				onOpenChange?: (isOpen: boolean) => void;
				isMenuOpen: boolean;
				// Pass-through props for EndOfPageHideConfirmModal:
				contentId: EndOfPageHideConfirmModalProps['entityId'];
				contentType?: EndOfPageHideConfirmModalProps['entityType'];
				spaceKey: EndOfPageHideConfirmModalProps['spaceKey'];
			} & Pick<ContentToolsButtonProps, 'isExternalCollaborator'> &
				Pick<ShortcutsProps, 'isAppNavigationShortcut' | 'keyboardShortcuts'>,
			keyof WithAnalyticsEventsProps
		>,
		| 'contentId'
		| 'contentType'
		| 'isExternalCollaborator'
		| 'spaceKey'
		| 'isAppNavigationShortcut'
		| 'onOpenChange'
		| 'itemSections'
		| 'isMenuOpen'
	> & {
		keyboardShortcuts?:
			| {
					deprecatedAccelerator?: string | string[] | undefined;
					accelerator: string | string[];
					itemId: string;
			  }[]
			| undefined;
	} & {} & React.RefAttributes<any>
> = withAnalyticsEvents()(ContentToolsComponentClass);

export type ContentToolsComponentProps = {
	itemSections: ReadonlyArray<NonNullable<ReactNode>>;
	onOpenChange?: (isOpen: boolean) => void;
	isMenuOpen: boolean;

	// Pass-through props for EndOfPageHideConfirmModal:
	contentId: EndOfPageHideConfirmModalProps['entityId'];
	contentType?: EndOfPageHideConfirmModalProps['entityType'];
	spaceKey: EndOfPageHideConfirmModalProps['spaceKey'];
} & Pick<
	ContentToolsButtonProps, // Pass-through props for ContentToolsButton:
	'isExternalCollaborator'
> &
	Pick<
		ShortcutsProps, // Pass-through props for Shortcuts:
		'isAppNavigationShortcut' | 'keyboardShortcuts'
	>;

type EndOfPageHideConfirmModalProps = ComponentProps<typeof EndOfPageHideConfirmModal>;
